// sessionTokenSync: keep tokens in sessionStorage (per-tab) but synchronize them across tabs
// using BroadcastChannel. This allows tabs in the same browser profile to share login state,
// while tokens live in sessionStorage and are cleared when the browser/process is closed.

import { store } from "reduxFiles/store";
import { setTokens, setAccessToken, logout } from "reduxFiles/slices/authSlice";
import { setResponderToken, clearResponderAuth } from "reduxFiles/slices/responderAuthSlice";

const CHANNEL_NAME = "vitalPlus-token-channel";
const bcSupported = typeof window.BroadcastChannel === "function";
let bc: BroadcastChannel | null = null;

function safeParse(s?: string) {
  try {
    return s ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
}

function readSessionTokens() {
  return {
    accessToken: sessionStorage.getItem("accessToken"),
    refreshToken: sessionStorage.getItem("refreshToken"),
    tokenType: sessionStorage.getItem("tokenType"),
    responderToken: sessionStorage.getItem("responder-token"),
  };
}

function writeSessionTokens(payload: any) {
  if (!payload) return;
  if (payload.accessToken) sessionStorage.setItem("accessToken", payload.accessToken);
  if (payload.refreshToken) sessionStorage.setItem("refreshToken", payload.refreshToken);
  if (payload.tokenType) sessionStorage.setItem("tokenType", payload.tokenType);
  if (payload.responderToken) sessionStorage.setItem("responder-token", payload.responderToken);
}

function clearSessionTokens() {
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("tokenType");
  sessionStorage.removeItem("responder-token");
}

// Create BroadcastChannel (fallback: null)
if (bcSupported) {
  try {
    bc = new BroadcastChannel(CHANNEL_NAME);
  } catch (e) {
    bc = null;
  }
}

function post(msg: any) {
  try {
    if (bc) {
      bc.postMessage(msg);
    } else {
      // fallback: use localStorage pump (short-lived key)
      localStorage.setItem(`${CHANNEL_NAME}-msg`, JSON.stringify({ ...msg, _ts: Date.now() }));
    }
  } catch (e) {
    // ignore
  }
}

// Respond to incoming messages
function handleMessage(msg: any) {
  if (!msg || !msg.type) return;

  if (msg.type === "request-token") {
    // another tab asks for token: if we have tokens in sessionStorage, reply
    const tokens = readSessionTokens();
    if (tokens.accessToken || tokens.responderToken) {
      post({ type: "share-token", payload: tokens });
    }
  }

  if (msg.type === "share-token") {
    // another tab shared tokens: if this tab lacks tokens, accept them
    const current = readSessionTokens();
    const payload = msg.payload || {};

    const needSetAuth = !current.accessToken && payload.accessToken;
    const needSetResponder = !current.responderToken && payload.responderToken;

    if (needSetAuth) {
      writeSessionTokens(payload);
      try {
        if (payload.accessToken) {
          // update redux: set full tokens if refreshToken exists, else just access
          if (payload.refreshToken || payload.tokenType) {
            store.dispatch(
              setTokens({
                accessToken: payload.accessToken,
                refreshToken: payload.refreshToken || "",
                tokenType: payload.tokenType || "",
              })
            );
          } else {
            store.dispatch(setAccessToken(payload.accessToken));
          }
        }
      } catch (e) {
        // ignore
      }
    }

    if (needSetResponder) {
      writeSessionTokens(payload);
      try {
        if (payload.responderToken) {
          store.dispatch(setResponderToken(payload.responderToken));
        }
      } catch (e) {}
    }
  }

  if (msg.type === "clear-token") {
    // clear tokens locally
    clearSessionTokens();
    try {
      store.dispatch(logout());
      store.dispatch(clearResponderAuth());
    } catch (e) {}
  }
}

// Listen to BroadcastChannel or localStorage fallback
if (bc) {
  bc.addEventListener("message", (ev) => {
    handleMessage(ev.data);
  });
} else {
  window.addEventListener("storage", (e) => {
    if (e.key === `${CHANNEL_NAME}-msg` && e.newValue) {
      const data = safeParse(e.newValue);
      handleMessage(data);
    }
  });
}

// On startup, request token from other tabs
try {
  post({ type: "request-token" });
} catch (e) {}

// Subscribe to store changes to broadcast token set/clear events
let lastAuth: any = null;
let lastResponderToken: string | null = null;

try {
  lastAuth = store.getState().auth;
  lastResponderToken = store.getState().responderAuth?.token || null;
} catch (e) {
  lastAuth = null;
}

store.subscribe(() => {
  try {
    const state = store.getState();
    const auth = state.auth;
    const responderToken = state.responderAuth?.token || null;

    // detect auth token changes
    if (!lastAuth || auth.accessToken !== lastAuth.accessToken || auth.refreshToken !== lastAuth.refreshToken || auth.tokenType !== lastAuth.tokenType) {
      lastAuth = { ...auth };

      if (auth.accessToken) {
        // write to sessionStorage and broadcast
        sessionStorage.setItem("accessToken", auth.accessToken);
        if (auth.refreshToken) sessionStorage.setItem("refreshToken", auth.refreshToken);
        if (auth.tokenType) sessionStorage.setItem("tokenType", auth.tokenType);

        post({ type: "share-token", payload: { accessToken: auth.accessToken, refreshToken: auth.refreshToken, tokenType: auth.tokenType } });
      } else {
        // cleared
        clearSessionTokens();
        post({ type: "clear-token" });
      }
    }

    // detect responder token changes
    if (responderToken !== lastResponderToken) {
      lastResponderToken = responderToken;
      if (responderToken) {
        sessionStorage.setItem("responder-token", responderToken);
        post({ type: "share-token", payload: { responderToken } });
      } else {
        sessionStorage.removeItem("responder-token");
        post({ type: "clear-token" });
      }
    }
  } catch (e) {
    // ignore
  }
});

// Expose nothing — module runs for side-effects
export {};
